<template>
  <div>
    <page-header
      icon="mdi-star-half-full"
      :title="$t('routes.meal-ratings')"
      :subtitle="$t('meal_rating.admin.subtitle')"
    >
      <template #actions>
        <excel-button
          :loading="exporting"
          class="mr-2"
          @click="exportExcel"
        />
        <circle-button icon="mdi-refresh" color="primary" :tooltip="$t('refresh')" @click="load" />
      </template>
    </page-header>

    <page-toolbar :searchable="false">
      <template #filters>
        <v-col v-if="isAdmin" cols="12" sm="6" lg="4">
          <autocomplete
            v-model="restaurantId"
            :items="restaurants"
            item-text="name"
            item-value="id"
            :label="$t('restaurant')"
            :placeholder="$t('meal_rating.admin.restaurant')"
            color="white"
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4">
          <date-picker
            v-model="dateRange"
            :label="$t('meal_rating.admin.period')"
            color="white"
          />
        </v-col>
      </template>
    </page-toolbar>

    <div :class="device.isMobile ? 'px-0 mt-2' : 'px-2 mt-2'">
      <v-progress-linear v-if="loading" indeterminate />

      <!-- Zbirno -->
      <v-row class="mb-2">
        <v-col cols="12" sm="4">
          <v-card outlined class="rounded-lg pa-4 fill-height">
            <div class="caption grey--text text--darken-1">{{ $t("meal_rating.admin.average") }}</div>
            <div class="d-flex align-center">
              <span class="text-h4 font-weight-bold mr-2">{{ formatAverage(summary.average) }}</span>
              <v-rating
                :value="summary.average || 0"
                readonly
                half-increments
                dense
                color="amber"
                background-color="grey lighten-1"
              />
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card outlined class="rounded-lg pa-4 fill-height">
            <div class="caption grey--text text--darken-1">{{ $t("meal_rating.admin.count") }}</div>
            <div class="text-h4 font-weight-bold">{{ summary.count || 0 }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card outlined class="rounded-lg pa-4 fill-height">
            <div class="caption grey--text text--darken-1 mb-1">{{ $t("meal_rating.admin.top_issues") }}</div>
            <template v-if="topIssues(summary.issues).length">
              <v-chip
                v-for="issue in topIssues(summary.issues)"
                :key="issue.code"
                small
                outlined
                color="primary"
                class="mr-1 mb-1"
              >
                {{ $t(`meal_rating.issues.${issue.code}`) }} · {{ issue.count }}
              </v-chip>
            </template>
            <span v-else class="grey--text">—</span>
          </v-card>
        </v-col>
      </v-row>

      <!-- Po obroku -->
      <h3 class="mb-2">{{ $t("meal_rating.admin.by_meal") }}</h3>
      <v-data-table
        class="ordera-box-shadow striped-table mb-6"
        :headers="mealHeaders"
        :items="meals"
        :loading="loading"
        sort-by="average"
        :sort-desc="false"
        :no-data-text="$t('meal_rating.admin.no_data')"
        dense
      >
        <template v-slot:item.meal="{ item }">
          {{ translate(item.meal ? item.meal.name : "") }}
        </template>
        <template v-slot:item.average="{ item }">
          <div class="d-flex align-center">
            <v-rating
              :value="item.average"
              readonly
              half-increments
              dense
              small
              color="amber"
              background-color="grey lighten-1"
            />
            <span class="ml-2">{{ formatAverage(item.average) }}</span>
          </div>
        </template>
        <template v-slot:item.issues="{ item }">
          <v-chip
            v-for="issue in topIssues(item.issues)"
            :key="issue.code"
            x-small
            outlined
            class="mr-1"
          >
            {{ $t(`meal_rating.issues.${issue.code}`) }} · {{ issue.count }}
          </v-chip>
        </template>
      </v-data-table>

      <!-- Pojedinačne ocene -->
      <h3 class="mb-2">{{ $t("meal_rating.admin.all_ratings") }}</h3>
      <v-data-table
        class="ordera-box-shadow striped-table"
        :headers="ratingHeaders"
        :items="ratings"
        :loading="loading"
        :no-data-text="$t('meal_rating.admin.no_data')"
        dense
      >
        <template v-slot:item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template v-slot:item.user="{ item }">
          {{ item.user ? toLatin(`${item.user.name || ""} ${item.user.lastname || ""}`) : "" }}
        </template>
        <template v-slot:item.meal="{ item }">
          {{ translate(item.meal ? item.meal.name : "") }}
        </template>
        <template v-slot:item.rating="{ item }">
          <v-rating
            :value="item.rating"
            readonly
            dense
            small
            color="amber"
            background-color="grey lighten-1"
          />
        </template>
        <template v-slot:item.issues="{ item }">
          <v-chip
            v-for="code in item.issues || []"
            :key="code"
            x-small
            outlined
            class="mr-1"
          >
            {{ $t(`meal_rating.issues.${code}`) }}
          </v-chip>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import restaurantService from "@/services/restaurant";
import hasSnackbar from "@/mixins/hasSnackbar";

export default {
  mixins: [hasSnackbar],
  data() {
    return {
      loading: false,
      exporting: false,
      restaurants: [],
      restaurantId: this.$store.state.restaurant?.id || null,
      dateRange: [
        moment().subtract(30, "days").format("YYYY-MM-DD"),
        moment().format("YYYY-MM-DD"),
      ],
      summary: {},
      meals: [],
      ratings: [],
    };
  },
  computed: {
    restaurantName() {
      const selected = this.restaurants.find((r) => r.id === this.restaurantId);
      return (selected || this.$store.state.restaurant || {}).name || "";
    },
    periodLabel() {
      const [from, to] = [...this.dateRange].sort();
      return `${moment(from).format("DD.MM.YYYY.")} - ${moment(to).format("DD.MM.YYYY.")}`;
    },
    isAdmin() {
      return this.$store.state.user.user?.type === "admin";
    },
    mealHeaders() {
      return [
        { text: this.$t("meal"), value: "meal", sortable: false },
        { text: this.$t("meal_rating.admin.count"), value: "count" },
        { text: this.$t("meal_rating.admin.average"), value: "average" },
        { text: this.$t("meal_rating.admin.issues"), value: "issues", sortable: false },
      ];
    },
    ratingHeaders() {
      return [
        { text: this.$t("date"), value: "created_at" },
        // Ime radnika vidi samo admin — backend ga restoranu i ne šalje
        ...(this.isAdmin
          ? [{ text: this.$t("meal_rating.admin.user"), value: "user", sortable: false }]
          : []),
        { text: this.$t("meal"), value: "meal", sortable: false },
        { text: this.$t("meal_rating.rating"), value: "rating" },
        { text: this.$t("meal_rating.admin.issues"), value: "issues", sortable: false },
        { text: this.$t("meal_rating.comment_label"), value: "comment", sortable: false },
      ];
    },
  },
  watch: {
    restaurantId: "load",
    dateRange: "load",
  },
  created() {
    if (this.isAdmin) this.loadRestaurants();
    this.load();
  },
  methods: {
    loadRestaurants() {
      restaurantService
        .getAllRestaurants()
        .then((res) => {
          this.restaurants = res.data || [];
        })
        .catch(() => {
          this.restaurants = [];
        });
    },
    load() {
      // Opseg je kompletan tek kad su izabrana oba datuma
      if (!this.restaurantId || !Array.isArray(this.dateRange) || this.dateRange.length < 2) return;
      this.loading = true;
      restaurantService
        .getMealRatings(this.restaurantId, {
          // Picker vraća datume redom kojim su kliknuti
          date_from: [...this.dateRange].sort()[0],
          date_to: [...this.dateRange].sort()[this.dateRange.length - 1],
        })
        .then((res) => {
          this.summary = res.data.summary || {};
          this.meals = res.data.meals || [];
          this.ratings = res.data.ratings || [];
        })
        .catch((error) => {
          this.showErrorSnackbar(
            error?.response?.data?.message || this.$t("meal_rating.admin.error_loading")
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async exportExcel() {
      if (!this.ratings.length) {
        this.showErrorSnackbar(this.$t("meal_rating.admin.no_data"));
        return;
      }
      this.exporting = true;
      try {
        const workbook = new ExcelJS.Workbook();
        const issuesText = (codes) =>
          (codes || []).map((code) => this.$t(`meal_rating.issues.${code}`)).join(", ");
        const countsText = (issues) =>
          this.topIssues(issues)
            .map((i) => `${this.$t(`meal_rating.issues.${i.code}`)} (${i.count})`)
            .join(", ");
        const styleHeader = (sheet) => {
          sheet.getRow(1).font = { bold: true };
          sheet.views = [{ state: "frozen", ySplit: 1 }];
        };

        // Zbirno
        const summary = workbook.addWorksheet(this.$t("meal_rating.admin.summary"));
        summary.columns = [{ width: 30 }, { width: 50 }];
        summary.addRows([
          [this.$t("restaurant"), this.restaurantName],
          [this.$t("meal_rating.admin.period"), this.periodLabel],
          [this.$t("meal_rating.admin.count"), this.summary.count || 0],
          [this.$t("meal_rating.admin.average"), this.summary.average || ""],
        ]);
        summary.addRow([]);
        summary.addRow([this.$t("meal_rating.admin.issues")]).font = { bold: true };
        Object.entries(this.summary.issues || {}).forEach(([code, count]) => {
          summary.addRow([this.$t(`meal_rating.issues.${code}`), count]);
        });
        summary.getColumn(1).font = { bold: true };

        // Po obroku
        const byMeal = workbook.addWorksheet(this.$t("meal_rating.admin.by_meal"));
        byMeal.columns = [
          { header: this.$t("meal"), key: "meal", width: 50 },
          { header: this.$t("meal_rating.admin.count"), key: "count", width: 14 },
          { header: this.$t("meal_rating.admin.average"), key: "average", width: 16 },
          { header: this.$t("meal_rating.admin.issues"), key: "issues", width: 50 },
        ];
        this.meals.forEach((m) => {
          byMeal.addRow({
            meal: this.translate(m.meal ? m.meal.name : ""),
            count: m.count,
            average: m.average,
            issues: countsText(m.issues),
          });
        });
        styleHeader(byMeal);

        // Sve ocene
        const all = workbook.addWorksheet(this.$t("meal_rating.admin.all_ratings"));
        all.columns = [
          { header: this.$t("date"), key: "date", width: 18 },
          ...(this.isAdmin
            ? [{ header: this.$t("meal_rating.admin.user"), key: "user", width: 28 }]
            : []),
          { header: this.$t("meal"), key: "meal", width: 50 },
          { header: this.$t("meal_rating.rating"), key: "rating", width: 10 },
          { header: this.$t("meal_rating.admin.issues"), key: "issues", width: 40 },
          { header: this.$t("meal_rating.comment_label"), key: "comment", width: 60 },
        ];
        this.ratings.forEach((r) => {
          all.addRow({
            date: this.formatDate(r.created_at),
            user: r.user ? this.toLatin(`${r.user.name || ""} ${r.user.lastname || ""}`.trim()) : "",
            meal: this.translate(r.meal ? r.meal.name : ""),
            rating: r.rating,
            issues: issuesText(r.issues),
            comment: r.comment || "",
          });
        });
        all.getColumn("comment").alignment = { wrapText: true, vertical: "top" };
        styleHeader(all);

        const buffer = await workbook.xlsx.writeBuffer();
        const [from, to] = [...this.dateRange].sort();
        saveAs(
          new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
          `ocene-obroka_${from}_${to}.xlsx`
        );
      } catch (error) {
        this.showErrorSnackbar(this.$t("meal_rating.admin.error_export"));
      } finally {
        this.exporting = false;
      }
    },
    topIssues(issues) {
      return Object.entries(issues || {})
        .filter(([, count]) => count > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([code, count]) => ({ code, count }));
    },
    formatAverage(value) {
      return value ? Number(value).toFixed(1) : "—";
    },
    formatDate(value) {
      return value ? moment(value).format("DD.MM.YYYY. HH:mm") : "";
    },
  },
};
</script>
